/**
 * Каталог целиком и поиск по нему.
 *
 * `CATALOG` — всё, что мы даём пользователю поставить на план, одной типизированной
 * константой: по разделу на слой, у раздела свой тип элемента. Никакого стора и
 * загрузки: движки читают элемент по ссылке из слоя прямо отсюда.
 */
import { CatalogSectionKind, type CatalogRef } from "@/shared/plan";
import { LayerKind } from "@/shared/plan/layerKind";
import { cableSection } from "./sections/cable";
import { baseboardSection } from "./sections/baseboard";
import { ceilingSection } from "./sections/ceiling";
import {
	customFurnitureItem,
	customFurnitureSection,
	isCustomFurnitureItem,
} from "./sections/custom";
import { decorSection } from "./sections/decor";
import { doorSection } from "./sections/door";
import { electricalSection } from "./sections/electrical";
import { finishSection } from "./sections/finish";
import { floorSection } from "./sections/floor";
import { furnitureSection } from "./sections/furniture";
import { lightingSection } from "./sections/lighting";
import { pipeSection } from "./sections/pipe";
import { plumbingSection } from "./sections/plumbing";
import { screedSection } from "./sections/screed";
import { wallSection } from "./sections/wall";
import { waterSection } from "./sections/water";
import { waterproofSection } from "./sections/waterproof";
import { windowSection } from "./sections/window";
import type { CatalogItem, CatalogItemBase, CatalogSection } from "./types";

export const CATALOG = {
	[CatalogSectionKind.Wall]: wallSection,
	[CatalogSectionKind.Door]: doorSection,
	[CatalogSectionKind.Window]: windowSection,
	[CatalogSectionKind.Floor]: floorSection,
	[CatalogSectionKind.Finish]: finishSection,
	[CatalogSectionKind.Waterproof]: waterproofSection,
	[CatalogSectionKind.Screed]: screedSection,
	[CatalogSectionKind.Ceiling]: ceilingSection,
	[CatalogSectionKind.Electrical]: electricalSection,
	[CatalogSectionKind.Lighting]: lightingSection,
	[CatalogSectionKind.Cable]: cableSection,
	[CatalogSectionKind.Furniture]: furnitureSection,
	[CatalogSectionKind.Decor]: decorSection,
	[CatalogSectionKind.CustomFurniture]: customFurnitureSection,
	[CatalogSectionKind.Plumbing]: plumbingSection,
	[CatalogSectionKind.Water]: waterSection,
	[CatalogSectionKind.Pipe]: pipeSection,
	[CatalogSectionKind.Baseboard]: baseboardSection,
	wall: wallSection,
	door: doorSection,
	window: windowSection,
	floor: floorSection,
	finish: finishSection,
	waterproof: waterproofSection,
	screed: screedSection,
	ceiling: ceilingSection,
	electrical: electricalSection,
	lighting: lightingSection,
	cable: cableSection,
	furniture: furnitureSection,
	decor: decorSection,
	customFurniture: customFurnitureSection,
	plumbing: plumbingSection,
	water: waterSection,
	pipe: pipeSection,
	baseboard: baseboardSection,
} as const;

export type CatalogSectionKey = CatalogSectionKind;

export const CATALOG_KEYS = [
	CatalogSectionKind.Wall,
	CatalogSectionKind.Door,
	CatalogSectionKind.Window,
	CatalogSectionKind.Floor,
	CatalogSectionKind.Finish,
	CatalogSectionKind.Waterproof,
	CatalogSectionKind.Screed,
	CatalogSectionKind.Ceiling,
	CatalogSectionKind.Electrical,
	CatalogSectionKind.Lighting,
	CatalogSectionKind.Cable,
	CatalogSectionKind.Furniture,
	CatalogSectionKind.Decor,
	CatalogSectionKind.CustomFurniture,
	CatalogSectionKind.Plumbing,
	CatalogSectionKind.Water,
	CatalogSectionKind.Pipe,
	CatalogSectionKind.Baseboard,
] as const;

/** Раздел по ключу — как элемент базового типа, без знания о его особых полях. */
export const catalogSection = (key: CatalogSectionKey): CatalogSection<CatalogItemBase> =>
	CATALOG[key];

/** Раздел исходного объекта каталога; поиск по ссылке на объект не путает одинаковые id. */
export const catalogSectionKeyOfItem = (item: CatalogItemBase): CatalogSectionKey | null =>
	isCustomFurnitureItem(item)
		? CatalogSectionKind.CustomFurniture
		: (CATALOG_KEYS.find((key) => CATALOG[key].items.includes(item as never)) ?? null);

/**
 * Разделы, чьи элементы ставятся из панели «Каталог» кликом по плану. Стены, двери,
 * окна, кабель и труба ставятся своими инструментами и в этот список не входят.
 */
const TOOL_LAYERS: readonly LayerKind[] = [LayerKind.Walls, LayerKind.Wiring, LayerKind.Pipes];
export const PLACEABLE_SECTION_KEYS: readonly CatalogSectionKey[] = CATALOG_KEYS.filter(
	(key) => !TOOL_LAYERS.includes(CATALOG[key].kind),
);

/**
 * Разделы объектов с габаритами и точкой установки — то, что ставится на план
 * объектом (`Fixture`, `FloorItem`). Пол, гидроизоляция и стяжка назначаются
 * комнате и здесь не значатся: у них свои функции поиска в разделах.
 */
const PLACED_SECTIONS: Partial<Record<CatalogSectionKey, CatalogSection<CatalogItem>>> = {
	[CatalogSectionKind.Electrical]: electricalSection,
	[CatalogSectionKind.Lighting]: lightingSection,
	[CatalogSectionKind.Furniture]: furnitureSection,
	[CatalogSectionKind.Decor]: decorSection,
	[CatalogSectionKind.CustomFurniture]: customFurnitureSection,
	[CatalogSectionKind.Plumbing]: plumbingSection,
	[CatalogSectionKind.Water]: waterSection,
};

/** Раздел, в котором лежит элемент по ссылке; null — тип удалён из каталога. */
export const sectionOfItem = (ref: CatalogRef): CatalogSection<CatalogItem> | null =>
	PLACED_SECTIONS[ref.catalog] ?? null;

/** Элемент каталога по ссылке из слоя; null — тип удалён из каталога или слой без объектов. */
export const getCatalogItem = (ref: CatalogRef): CatalogItem | null =>
	ref.catalog === CatalogSectionKind.CustomFurniture
		? customFurnitureItem(ref.item)
		: (sectionOfItem(ref)?.items.find((item) => item.id === ref.item) ?? null);

/** Мягкая мебель: обивку можно сменить на ткань или кожу из библиотеки (`FloorItem.fabric`). */
export const isUpholstered = (item: CatalogItem | null): boolean =>
	item?.view3d.type === "model" && item.view3d.upholstery === true;

/** Все объекты, которые могут лежать в слое `kind`; пусто — слой не про объекты каталога. */
export const catalogItemsOf = (kind: LayerKind): readonly CatalogItem[] =>
	Object.values(PLACED_SECTIONS)
		.filter((section): section is CatalogSection<CatalogItem> => section?.kind === kind)
		.flatMap((section) => section.items);

/** Соседи элемента по его разделу — на них можно сменить тип, не переставляя объект. */
export const catalogSiblings = (ref: CatalogRef): readonly CatalogItem[] =>
	sectionOfItem(ref)?.items ?? [];

/** Ссылка на первый элемент каталога — с него начинает инструмент установки. */
export const firstCatalogRef = (): CatalogRef | null => {
	const key = PLACEABLE_SECTION_KEYS[0];
	const section = key ? CATALOG[key] : null;
	const item = section?.items[0];
	return key && section && item ? { catalog: key, item: item.id } : null;
};
